/**
 * Gift Modal — Global Event Wiring
 *
 * Registers all document-level event listeners that connect the gift modal
 * to the rest of the page: opening from PDP/cart drawer, syncing state on
 * variant/quantity changes, deep-link support, and cart drawer MutationObserver.
 *
 * Loaded AFTER gift-box-customizer-modal.js so the custom element and
 * window.openGiftBoxModal are available.
 */
(function initGiftModalWiring(global) {
  'use strict';

  if (global.__snowcubesGiftModalWiringLoaded) return;

  var Gift = global.SnowcubesGift || {};

  function translate(key, values, fallback) {
    var table = global.SnowcubesLocale || {};
    var value = table[key];
    if (typeof value !== 'string' || !value) value = fallback || key;
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, function (_match, name) {
      return values && values[name] !== undefined ? String(values[name]) : '';
    });
  }

  // Global helper — opens gift modal (supports multiple gift notes per order)
  global.openGiftBoxModal = function openGiftBoxModal(options) {
    var modal = document.querySelector('gift-box-customizer-modal');
    if (!modal) return;

    if (Gift.debug) Gift.debug('openGiftBoxModal', { mode: (options && options.mode) || 'add' });
    modal.open(options);
  };

  // ── Initial state + deep-link ──
  // This file is demand-loaded and may arrive after DOMContentLoaded.
  function onGiftDOMReady() {
    if (Gift.updateGiftNoteCardState) Gift.updateGiftNoteCardState({ animate: false });
    document.querySelectorAll('gift-note-trigger').forEach(function (t) {
      if (t.updateState) t.updateState({ animate: false });
    });

    // ?gift=1 deep-link: auto-open gift modal on PDP (for homepage/email CTAs)
    if (new URLSearchParams(global.location.search).get('gift') === '1') {
      setTimeout(function () {
        var trigger = document.querySelector('gift-note-trigger');
        if (global.openGiftBoxModal) {
          global.openGiftBoxModal({
            mode: 'add',
            trigger: trigger || null,
            entryAction: 'deeplink',
          });
        }
      }, 600);
    }
  }

  // ── gift-box-submit: sync gift note card state after save ──
  document.addEventListener('gift-box-submit', function () {
    if (Gift.updateGiftNoteCardState) Gift.updateGiftNoteCardState();
  });

  // ── Variant / quantity change: sync gift note card state ──
  document.addEventListener('change', function (e) {
    var variantSelect = e.target.closest('variant-selects, variant-radios');
    var quantityInput = e.target && e.target.name === 'quantity' ? e.target : null;
    if (variantSelect || quantityInput) {
      var productForm = variantSelect?.resolveOwningProductForm?.() ||
        quantityInput?.form || quantityInput?.closest?.('form') || null;
      if (Gift.updateGiftNoteCardState) {
        Gift.updateGiftNoteCardState({
          variantSelect: variantSelect || null,
          productForm,
        });
      }
      document.querySelectorAll('gift-note-trigger').forEach(function (t) {
        if (t.updateState) t.updateState();
      });
    }
  });

  document.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'quantity') {
      if (Gift.updateGiftNoteCardState) {
        Gift.updateGiftNoteCardState({
          productForm: e.target.form || e.target.closest?.('form') || null,
        });
      }
      document.querySelectorAll('gift-note-trigger').forEach(function (t) {
        if (t.updateState) t.updateState();
      });
    }
  });

  // ── PDP gift note card handler (avoid inline onclick) ──
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gift-note-open]');
    if (!btn) return;

    var mode = btn.dataset.mode || 'add';
    var options = {
      mode: mode,
      trigger: btn.closest('gift-note-trigger') || null,
      entryAction: 'customize_button',
    };

    if (mode === 'edit' && global.giftConfigPrototype && global.giftConfigPrototype.load) {
      var triggerEl = options.trigger;
      var productHandle =
        (triggerEl && triggerEl.productHandle) ||
        (Gift.productHandleFromElement && Gift.productHandleFromElement(triggerEl)) ||
        null;
      // Prefer handle-scoped load so edit never pulls a neighbour card's note.
      var config = productHandle
        ? global.giftConfigPrototype.load(productHandle)
        : null;
      if (config) {
        options.config = config;
      } else {
        options.mode = 'add';
      }
    }

    global.openGiftBoxModal(options);
  });

  // ── Cart drawer edit button handler (event delegation) ──
  document.addEventListener('click', function (e) {
    var editBtn = e.target.closest('[data-edit-gift]');
    if (!editBtn) return;

    var variantId = parseInt(editBtn.dataset.variantId, 10);
    var itemKey = editBtn.dataset.itemKey;
    var configJson = editBtn.dataset.config;

    try {
      var config = JSON.parse(configJson);
      if (config.schema !== 'snowcubes.giftCard.v1') {
        console.warn('Unknown gift config schema:', config.schema);
      }
      global.openGiftBoxModal({
        mode: 'edit',
        variantId: variantId,
        itemKey: itemKey,
        config: config,
        entryAction: 'cart_edit',
      });
    } catch (err) {
      console.error('Failed to parse gift config:', err);
      var modal = document.querySelector('gift-box-customizer-modal');
      if (modal) {
        modal.showError(translate('pages.gift_modal.error_open'));
      }
    }
  });

  // ── Render gift summaries after cart drawer updates ──
  function onGiftSummariesInit() {
    if (Gift.renderGiftSummaries) Gift.renderGiftSummaries();

    var cartDrawer = document.querySelector(
      '#dialog-cart-1, #dialog-cart-2, [data-cart-drawer], [data-section="cart-drawer"]'
    );
    if (!cartDrawer || !Gift.renderGiftSummaries) return;

    if (global.__giftCartDrawerObserver) {
      global.__giftCartDrawerObserver.disconnect();
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          Gift.renderGiftSummaries(cartDrawer);
          break;
        }
      }
    });

    global.__giftCartDrawerObserver = observer;
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  function initializeReadyState() {
    onGiftDOMReady();
    onGiftSummariesInit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeReadyState, { once: true });
  } else {
    initializeReadyState();
  }

  // Mark ready only after listener setup succeeds so a failed evaluation can
  // be retried by the demand loader.
  global.__snowcubesGiftModalWiringLoaded = true;
  if (Gift.debug) Gift.debug('Gift Modal Wiring loaded');
})(typeof window !== 'undefined' ? window : globalThis);
